import { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { FileText, Clock, CheckCircle, AlertCircle, Home, Settings, BarChart3, FileStack, ChevronRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';

const mockJobs = [
  {
    id: 'job_001',
    filename: 'invoice_acme_corp.pdf',
    status: 'completed',
    created: '2 min ago',
    data: {
      vendor: 'Acme Corporation',
      invoice_number: 'INV-2024-0892',
      date: '2024-01-15',
      due_date: '2024-02-15',
      subtotal: 1050.0,
      tax: 200.0,
      total: 1250.0,
      currency: 'USD',
      items: [
        { description: 'Widget Pro X', quantity: 10, unit_price: 75.0 },
        { description: 'Service Fee', quantity: 1, unit_price: 300.0 },
      ],
    },
  },
  {
    id: 'job_002',
    filename: 'receipt_coffee_shop.jpg',
    status: 'completed',
    created: '5 min ago',
    data: {
      merchant: 'Blue Bottle Coffee',
      date: '2024-01-14',
      items: [
        { description: 'Latte', quantity: 2, price: 9.5 },
        { description: 'Croissant', quantity: 1, price: 4.5 },
      ],
      subtotal: 14.0,
      tax: 1.12,
      total: 15.12,
      payment_method: 'Credit Card',
    },
  },
  {
    id: 'job_003',
    filename: 'po_order_supplies.pdf',
    status: 'processing',
    created: '1 min ago',
    data: null,
  },
  {
    id: 'job_004',
    filename: 'id_card_scan.png',
    status: 'failed',
    created: '10 min ago',
    data: null,
    error: 'Image quality too low for extraction',
  },
];

const StatusBadge = ({ status }) => {
  const variants = {
    completed: 'bg-green-500/20 text-green-400 border-green-500/30',
    processing: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    failed: 'bg-red-500/20 text-red-400 border-red-500/30',
  };

  const icons = {
    completed: <CheckCircle className="w-3 h-3 mr-1" />,
    processing: <Clock className="w-3 h-3 mr-1 animate-spin" />,
    failed: <AlertCircle className="w-3 h-3 mr-1" />,
  };

  return (
    <Badge variant="outline" className={`${variants[status]} text-xs font-medium capitalize`}>
      {icons[status]}
      {status}
    </Badge>
  );
};

const JSONViewer = ({ data }) => {
  if (!data) {
    return (
      <div className="flex items-center justify-center h-full text-white/40">
        <p>No data available</p>
      </div>
    );
  }

  const renderValue = (value, depth = 0) => {
    if (Array.isArray(value)) {
      return (
        <span>
          <span className="text-white">{'['}</span>
          {value.map((item, i) => (
            <div key={i} style={{ paddingLeft: `${(depth + 1) * 16}px` }}>
              {renderValue(item, depth + 1)}
              {i < value.length - 1 && <span className="text-white">,</span>}
            </div>
          ))}
          <span style={{ paddingLeft: `${depth * 16}px` }} className="text-white">
            {']'}
          </span>
        </span>
      );
    }

    if (typeof value === 'object' && value !== null) {
      const entries = Object.entries(value);
      return (
        <span>
          <span className="text-white">{'{'}</span>
          {entries.map(([key, val], i) => (
            <div key={key} style={{ paddingLeft: `${(depth + 1) * 16}px` }}>
              <span className="text-[#89DDFF]">"{key}"</span>
              <span className="text-white">: </span>
              {renderValue(val, depth + 1)}
              {i < entries.length - 1 && <span className="text-white">,</span>}
            </div>
          ))}
          <span style={{ paddingLeft: `${depth * 16}px` }} className="text-white">
            {'}'}
          </span>
        </span>
      );
    }

    if (typeof value === 'string') {
      return <span className="text-[#C3E88D]">"{value}"</span>;
    }

    if (typeof value === 'number') {
      return <span className="text-[#F78C6C]">{value}</span>;
    }

    return <span className="text-white">{String(value)}</span>;
  };

  return (
    <pre className="font-mono text-xs leading-relaxed overflow-auto">{renderValue(data)}</pre>
  );
};

export const DashboardPreview = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const [selectedJob, setSelectedJob] = useState(mockJobs[0]);

  const sidebarItems = [
    { icon: Home, active: false },
    { icon: FileStack, active: true },
    { icon: BarChart3, active: false },
    { icon: Settings, active: false },
  ];

  return (
    <section
      ref={ref}
      className="py-20 md:py-28 bg-[#F7F8FC]"
      data-testid="dashboard-preview-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <span className="inline-block px-4 py-1.5 bg-[#635BFF]/10 text-[#635BFF] text-sm font-medium rounded-full mb-4">
            Dashboard
          </span>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-[#0A2540] mb-6">
            Monitor Your OCR Jobs
          </h2>
          <p className="text-lg text-[#425466] leading-relaxed">
            Track processing status, view extracted data, and manage your documents all in one place.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="rounded-3xl overflow-hidden shadow-2xl border border-white/10"
        >
          <div className="bg-[#0A2540] flex min-h-[480px]">
            {/* Sidebar */}
            <div className="w-16 bg-[#0D2E4D] flex flex-col items-center py-6 gap-4 border-r border-white/5">
              <div className="w-8 h-8 bg-[#635BFF] rounded-lg flex items-center justify-center mb-4">
                <FileText className="w-4 h-4 text-white" />
              </div>
              {sidebarItems.map((item, index) => (
                <button
                  key={index}
                  className={`w-10 h-10 rounded-xl flex items-center justify-center transition-colors duration-200 ${
                    item.active
                      ? 'bg-[#635BFF]/20 text-[#635BFF]'
                      : 'text-white/40 hover:text-white/70 hover:bg-white/5'
                  }`}
                >
                  <item.icon className="w-5 h-5" />
                </button>
              ))}
            </div>

            {/* Job List */}
            <div className="w-72 bg-[#0F3354] border-r border-white/5">
              <div className="p-4 border-b border-white/5">
                <h3 className="text-white font-semibold text-sm">OCR Jobs</h3>
                <p className="text-white/40 text-xs mt-1">{mockJobs.length} documents</p>
              </div>
              <ScrollArea className="h-[400px]">
                {mockJobs.map((job) => (
                  <button
                    key={job.id}
                    onClick={() => setSelectedJob(job)}
                    className={`w-full p-4 text-left border-b border-white/5 transition-colors duration-200 ${
                      selectedJob.id === job.id
                        ? 'bg-[#635BFF]/20'
                        : 'hover:bg-white/5'
                    }`}
                    data-testid={`job-item-${job.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-white/60 flex-shrink-0" />
                          <span className="text-white text-sm font-medium truncate">
                            {job.filename}
                          </span>
                        </div>
                        <p className="text-white/40 text-xs mt-1 ml-6">{job.created}</p>
                      </div>
                      <ChevronRight className={`w-4 h-4 text-white/30 flex-shrink-0 transition-transform duration-200 ${
                        selectedJob.id === job.id ? 'rotate-90' : ''
                      }`} />
                    </div>
                    <div className="mt-2 ml-6">
                      <StatusBadge status={job.status} />
                    </div>
                  </button>
                ))}
              </ScrollArea>
            </div>

            {/* Detail View */}
            <div className="flex-1 flex flex-col">
              <div className="p-4 border-b border-white/5 flex items-center justify-between">
                <div>
                  <h3 className="text-white font-semibold text-sm">{selectedJob.filename}</h3>
                  <p className="text-white/40 text-xs mt-1">ID: {selectedJob.id}</p>
                </div>
                <StatusBadge status={selectedJob.status} />
              </div>
              <div className="flex-1 p-4 overflow-auto">
                <div className="bg-[#1E1E3F] rounded-xl p-4 h-full">
                  <div className="flex items-center gap-2 mb-4 pb-3 border-b border-white/10">
                    <div className="w-3 h-3 rounded-full bg-red-400" />
                    <div className="w-3 h-3 rounded-full bg-yellow-400" />
                    <div className="w-3 h-3 rounded-full bg-green-400" />
                    <span className="ml-2 text-xs text-white/50">output.json</span>
                  </div>
                  {selectedJob.status === 'failed' ? (
                    <div className="flex items-center gap-3 text-red-400">
                      <AlertCircle className="w-5 h-5" />
                      <span className="text-sm">{selectedJob.error}</span>
                    </div>
                  ) : selectedJob.status === 'processing' ? (
                    <div className="flex items-center gap-3 text-yellow-400">
                      <Clock className="w-5 h-5 animate-spin" />
                      <span className="text-sm">Processing document...</span>
                    </div>
                  ) : (
                    <JSONViewer data={selectedJob.data} />
                  )}
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
